import { createTheme } from "@mui/material/styles";

// Define custom color palette
const baseColors = {
  defaultBackground: "#FFFFFF",
  primary: {
    0: "#CDE4FF",
    1: "#A4CEFF",
    2: "#7AB8FF",
    3: "#52A2FF",
    4: "#288BFE",
    5: "#0C6FE2",
    6: "#0056B9",
    7: "#003878",
    8: "#001834",
  },
  secondary: {
    0: "#F8F8FB",
    1: "#E5E5EA",
    2: "#D1D1D6",
    3: "#C7C7CC",
    4: "#AEAEB2",
    5: "#8E8E93",
    6: "#636366",
    7: "#48484A",
    8: "#3A3A3C",
  },
  text: {
    0: "#8E8E93",
    1: "#636366",
    2: "#48484A",
    3: "#3A3A3C",
    4: "#2C2C2E",
    5: "#1C1C1E",
    6: "#09090A",
    7: "#09090A",
    8: "#FCFCFC",
  },
  red: "#FF584E",
  orange: "#FF9914",
  green: "#2DD282",
  grey: "#F2F2F7",
  purple: "#3A26F5",
  metrics: {
    hoursInVr: "#288BFE",
    userSessions: "#2DD282",
    uniqueActiveUsers: "#503DFF",
    certifications: "#42B7B7",
  },
  feedback: {
    practice: "#B24EDA",
    assessment: "#906BFF",
    starFilled: "#F9C806",
    starEmpty: "#8E8E93",
    starDisabled: "#D1D1D6",
  },
};

// Extend the Material-UI theme interface
declare module "@mui/material/styles" {
  interface Theme {
    colors: typeof baseColors;
  }

  interface ThemeOptions {
    colors?: typeof baseColors;
  }
}

// Extend Button variant types
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    secondary: true;
  }
}

// Create a theme instance with custom colors and typography
export const theme = createTheme({
  colors: baseColors,
  palette: {
    primary: {
      main: baseColors.primary[5],
      light: baseColors.primary[2],
      dark: baseColors.primary[6],
    },
    secondary: {
      main: baseColors.secondary[4],
      light: baseColors.secondary[2],
      dark: baseColors.secondary[6],
    },
    background: {
      default: baseColors.defaultBackground,
      paper: baseColors.defaultBackground,
    },
    text: {
      primary: baseColors.text[6],
      secondary: baseColors.text[1],
    },
  },
  typography: {
    fontFamily: ["Poppins", '"Helvetica Neue"', "Arial", "sans-serif"].join(
      ",",
    ),
    h1: {
      fontSize: "2.125rem",
      fontWeight: 500,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 500,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          scrollBehavior: "smooth",
        },
        "*::-webkit-scrollbar": {
          width: "12px",
          height: "12px",
        },
        "*::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          background: baseColors.secondary[3],
          borderRadius: "10px",
          border: `4px solid transparent`,
          backgroundClip: "padding-box",
          transition: "transform 0.2s ease",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: baseColors.secondary[5],
          borderRadius: "10px",
          border: `3px solid transparent`,
          backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-button": {
          display: "none",
          width: "0",
          height: "0",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    // Override component styles here if needed
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Disable uppercase transformation
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:disabled": {
            backgroundColor: baseColors.primary[2],
            color: baseColors.defaultBackground,
          },
        },
      },
      variants: [
        {
          props: { variant: "secondary" },
          style: {
            padding: "8px 16px",
            color: baseColors.red,
            backgroundColor: baseColors.grey,
            fontWeight: 600,
            fontSize: 14,
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: baseColors.grey,
            },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 32,
          height: 20,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "4px 4px",
            transition: "transform 300ms, color 300ms",
            color: baseColors.primary[5],
            "&.Mui-checked": {
              transform: "translateX(12px)",
              color: baseColors.defaultBackground,
              "& + .MuiSwitch-track": {
                backgroundColor: baseColors.primary[5],
                opacity: 1,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: baseColors.secondary[2],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.7,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 12,
            height: 12,
            boxShadow: "none",
          },
          "& .MuiSwitch-track": {
            borderRadius: 10,
            backgroundColor: baseColors.defaultBackground,
            opacity: 1,
            border: `2px solid ${baseColors.primary[5]}`,
          },
        },
      },
    },
  },
});
